import { convexClient, hasLiveConvex } from './convexClient';
import { idbStorage } from './idbStorage';

export const profileService = {
  async getProfile(userId) {
    try {
      if (hasLiveConvex) {
        const profile = await convexClient.query("profiles:getProfile", { userId });
        if (profile) return profile;
      }
      
      const localProfiles = JSON.parse(localStorage.getItem('profiles') || '{}');
      return localProfiles[userId] || {
        userId,
        fullName: 'Unknown User',
        username: 'unknown',
        bio: '',
        avatarUrl: null,
        coverUrl: null,
        location: '',
        website: '',
        joinedDate: new Date().toISOString(),
        followersCount: 0,
        followingCount: 0,
        friendsCount: 0
      };
    } catch (error) {
      console.error('Error fetching profile:', error);
      const localProfiles = JSON.parse(localStorage.getItem('profiles') || '{}');
      return localProfiles[userId] || null;
    }
  },

  async getAllProfiles() {
    try {
      if (hasLiveConvex) {
        // Fallback for live Convex
        return await convexClient.query("profiles:getAllProfiles") || [];
      }
      
      const localProfiles = JSON.parse(localStorage.getItem('profiles') || '{}');
      return Object.values(localProfiles);
    } catch (error) {
      console.error('Error fetching all profiles:', error);
      return [];
    }
  },

  async updateProfile(userId, updates) {
    try {
      if (hasLiveConvex) {
        await convexClient.mutation("profiles:updateProfile", { userId, ...updates });
      }
      
      const localProfiles = JSON.parse(localStorage.getItem('profiles') || '{}');
      localProfiles[userId] = { ...localProfiles[userId], ...updates };
      localStorage.setItem('profiles', JSON.stringify(localProfiles));
      
      // Also update auth user if it's the current user
      const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
      if (currentUser && currentUser.id === userId) {
        const updatedUser = { ...currentUser, ...updates };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  },

  async uploadAvatar(userId, file) {
    if (!file) return null;
    try {
      if (hasLiveConvex) {
        // Fallback for live Convex if not configured
      }
      
      const tempUrl = URL.createObjectURL(file);
      const base64Url = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error("Could not get canvas context");
            const MAX_SIZE = 400;
            let width = img.width || 400;
            let height = img.height || 400;
            if (width > height && width > MAX_SIZE) {
              height *= MAX_SIZE / width; width = MAX_SIZE;
            } else if (height > MAX_SIZE) {
              width *= MAX_SIZE / height; height = MAX_SIZE;
            }
            canvas.width = Math.floor(width);
            canvas.height = Math.floor(height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = () => reject(new Error("Avatar compression failed"));
        img.src = tempUrl;
      });
      
      return base64Url;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return URL.createObjectURL(file); // absolute fallback
    }
  },

  async searchProfiles(query) {
    try {
      if (hasLiveConvex) {
        // We'd normally have a search query here, but we can just fetch all and filter for now
        // Or if we had a search endpoint in convex:
        // return await convexClient.query("profiles:search", { searchTerm: query });
      }
      
      const localProfiles = JSON.parse(localStorage.getItem('profiles') || '{}');
      const searchTerm = query.toLowerCase();
      return Object.values(localProfiles).filter(p => 
        (p.fullName && p.fullName.toLowerCase().includes(searchTerm)) || 
        (p.username && p.username.toLowerCase().includes(searchTerm))
      );
    } catch (error) {
      console.error('Error searching profiles:', error);
      return [];
    }
  }
};
